/**
 * LanguageSwitcher — compact UI control for switching the interface language.
 *
 * Variants:
 *   compact (default) — icon + active language code, expands to a small menu.
 *   inline           — shows all language chips in a row (for settings/profile pages).
 */
import React, { useState } from "react";
import {
  Box, Menu, MenuItem, Tooltip, IconButton,
  Typography, Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LanguageIcon from "@mui/icons-material/Language";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { SUPPORTED_UI_LANGS } from "./index.js";

// ── Compact dropdown (sidebar / topbar) ──────────────────────────────────────
// variant="dark"  — white text, for the dark sidebar (default)
// variant="light" — ink text, for headers and auth pages on light/sky backgrounds
export function LanguageSwitcherCompact({ sx = {}, variant = "dark" }) {
  const { i18n, t } = useTranslation();
  const [anchor, setAnchor] = useState(null);

  const currentLang = SUPPORTED_UI_LANGS.find((l) => l.code === i18n.language)
    || SUPPORTED_UI_LANGS[0];

  const handleOpen  = (e) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);
  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    handleClose();
  };

  const isDark = variant === "dark";

  return (
    <>
      <Tooltip title="Interface language / لغة الواجهة">
        <Box
          onClick={handleOpen}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleOpen(e)}
          aria-haspopup="true"
          aria-expanded={Boolean(anchor)}
          aria-label={t("switchLanguage")}
          sx={{
            display: "flex", alignItems: "center", gap: 0.75,
            px: 1.25, py: 0.75, borderRadius: "8px",
            cursor: "pointer",
            color: isDark ? "rgba(255,255,255,0.72)" : "var(--ink-500)",
            transition: "all 0.15s ease",
            "&:hover": isDark
              ? { background: "rgba(255,255,255,0.08)", color: "#fff" }
              : { background: "rgba(0,0,0,0.05)", color: "var(--ink-900)" },
            ...sx,
          }}
        >
          <LanguageIcon sx={{ fontSize: 17, flexShrink: 0 }} />
          <Typography sx={{
            fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.02em",
            color: "inherit", lineHeight: 1, textTransform: "uppercase",
          }}>
            {currentLang.code}
          </Typography>
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 160, borderRadius: "12px",
              border: "1px solid var(--line-200)",
              boxShadow: "var(--shadow-card)",
              py: 0.5,
            },
          },
        }}
      >
        {SUPPORTED_UI_LANGS.map((lang) => {
          const active = lang.code === i18n.language;
          return (
            <MenuItem
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              selected={active}
              sx={{
                gap: 1.5, minHeight: 38, px: 1.5,
                borderRadius: "8px", mx: 0.5, my: 0.25,
              }}
            >
              <Typography sx={{ flex: 1, fontSize: "0.875rem", fontWeight: active ? 700 : 500 }}>
                {lang.label}
              </Typography>
              {active && <CheckIcon sx={{ fontSize: 16, color: "primary.main" }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

// ── Inline chips (profile / settings page) ───────────────────────────────────
export function LanguageSwitcherInline({ sx = {} }) {
  const { i18n } = useTranslation();

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", ...sx }}>
      {SUPPORTED_UI_LANGS.map((lang) => {
        const active = lang.code === i18n.language;
        return (
          <Chip
            key={lang.code}
            label={lang.label}
            onClick={() => i18n.changeLanguage(lang.code)}
            variant={active ? "filled" : "outlined"}
            color={active ? "primary" : "default"}
            sx={{
              fontWeight: active ? 700 : 500,
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": { boxShadow: "0 0 0 2px var(--brand-violet)" },
            }}
          />
        );
      })}
    </Box>
  );
}

// Default export = compact variant
export default LanguageSwitcherCompact;
